import React, { useState, useMemo } from 'react';
import { BookOpen, Download, Lock, Unlock, RefreshCw, Search, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import LedgerSalidaDetalle from './LedgerSalidaDetalle';
import { useLedgerPage } from '../hooks/useLedgerPage';
import { useLedgerAudit } from '../hooks/useLedgerAudit';
import { useLedgerCandidates } from '../hooks/useLedgerCandidates';

// Sugerencia de cliente para un movimiento pending, segun el historial del remitente (CUIT).
// Backend: routes/bancos/ledger_sugerencias.py
const LedgerSugerencia = ({ s, movId }) => {
  if (!s) return <span className="text-[10px] text-gray-300">—</span>;
  const dias = s.dias ?? 0;
  const antiguedad = (
    <span className={`text-[9px] ${dias > 7 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
      {dias === 0 ? 'hoy' : `hace ${dias} ${dias === 1 ? 'día' : 'días'}`}
    </span>
  );
  return (
    <div className="flex flex-col gap-0.5 leading-tight" data-testid={`ledger-sugerencia-${movId}`}>
      {s.cliente_name && (
        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 font-semibold w-fit"
          title={`Este remitente ya fue conciliado ${s.veces} ${s.veces === 1 ? 'vez' : 'veces'} con ${s.cliente_name}${s.ultima_fecha ? ` · último ${s.ultima_fecha}` : ''}`}>
          💡 {s.cliente_name}
          <span className="font-normal opacity-70">({s.veces}{s.ultima_fecha ? ` · ${s.ultima_fecha.slice(5)}` : ''})</span>
        </span>
      )}
      {s.ambiguo && (
        <span className="text-[9px] text-amber-700 dark:text-amber-400" title={`Este remitente aparece con ${s.clientes} clientes distintos: no se sugiere para no confundir`}>
          historial ambiguo ({s.clientes})
        </span>
      )}
      {s.nuevo && <span className="text-[9px] text-gray-400">remitente nuevo</span>}
      {antiguedad}
    </div>
  );
};

// FASE 1 — Modal Ledger 1TouchPay (libro contable, read-only)
// ============================================================
// Lee /api/bancos/1touchpay/ledger (lista paginada con filtros) y
// /api/bancos/1touchpay/ledger/audit (cuadre + tamper check + saldos).
// Permite ver el estado real del ledger persistido en Mongo, separado
// por pending/consumed, con info del receipt vinculado si corresponde.
// ============================================================
export default function OneTouchPayLedgerModal({ initialCvuId, bankAccounts = [], clientes = [], onClose, onBalancesChanged }) {
  // Filtros — si vino initialCvuId, pre-filtramos por CVU para aislar el libro
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | consumed
  const [cvuFilter, setCvuFilter] = useState(initialCvuId ? String(initialCvuId) : '');
  const [tipoFilter, setTipoFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const { items, pagination, loading, error: ledgerError, refresh: fetchData, refreshRow } = useLedgerPage({
    status: statusFilter, cvu: cvuFilter, tipo: tipoFilter, estado: estadoFilter, page, search: appliedSearch,
  });
  const { audit, cuadre, checkedAt, error: auditError, loading: auditLoading, refresh: refreshAudit } = useLedgerAudit();
  const cvuOptions = useMemo(() => {
    const accounts = new Map(bankAccounts.filter(a => a.bank_key === '1touchpay' && a.cvu_id != null)
      .map(a => [String(a.cvu_id), { id: String(a.cvu_id), alias: a.alias || a.name || '' }]));
    if (initialCvuId != null && !accounts.has(String(initialCvuId))) accounts.set(String(initialCvuId), { id: String(initialCvuId), alias: '' });
    return [...accounts.values()].sort((a, b) => a.id === String(initialCvuId) ? -1 : b.id === String(initialCvuId) ? 1 : Number(a.id) - Number(b.id));
  }, [bankAccounts, initialCvuId]);
  const handleRowSaved = async ({ otpId, kind }) => {
    await refreshRow(otpId);
    if (kind !== 'nota') onBalancesChanged?.();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportUrl = cvuFilter ? `${API_URL}/api/bancos/1touchpay/ledger/export?cvu_id=${cvuFilter}` : `${API_URL}/api/bancos/1touchpay/ledger/export`;
      const res = await fetch(exportUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const ts = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '');
        a.download = `ledger_1touchpay_${ts}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Export ledger error', res.status);
      }
    } catch (e) {
      console.error('Export ledger error', e);
    } finally {
      setExporting(false);
    }
  };

  // ── Conciliación manual desde el ledger ──
  const [matchMov, setMatchMov] = useState(null);       // mov del ledger a conciliar
  const [matchSearch, setMatchSearch] = useState('');
  const [matching, setMatching] = useState(false);
  const { candidates, loading: loadingCand, error: candidatesError, search: findCandidates } = useLedgerCandidates(matchMov);

  const openMatch = (mov) => {
    setMatchMov(mov);
    setMatchSearch('');
  };

  const searchCandidates = () => findCandidates(matchSearch);

  const doMatch = async (receipt) => {
    if (!matchMov) return;
    setMatching(true);
    try {
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/ledger/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otp_id: matchMov.otp_id, receipt_id: receipt.id }),
      });
      if (res.ok) {
        setMatchMov(null);
        onBalancesChanged?.();
        refreshAudit();
        await fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`No se pudo conciliar: ${err.detail || res.status}`);
      }
    } catch (e) { alert('Error de red al conciliar'); } finally { setMatching(false); }
  };

  // ── Liberar (rematch) un movimiento consumido ──
  const [releasing, setReleasing] = useState(null); // otp_id en proceso de liberar
  const doRelease = async (mov) => {
    const info = mov._receipt_info || {};
    const desync = info.synced === false;
    const msg = desync
      ? `Liberar el movimiento ${mov.otp_id} (${money(mov.importe)})?\n\nEste consumo está DESINCRONIZADO (sin match real en el dashboard). Volverá a estado Pending para poder re-conciliarlo.`
      : `⚠️ Liberar el movimiento ${mov.otp_id} (${money(mov.importe)})?\n\nEste consumo SÍ tiene un match válido con el comprobante de "${info.cliente_name || '?'}". Si lo liberás, el comprobante quedará SIN conciliar de nuevo.`;
    if (!window.confirm(msg)) return;
    setReleasing(mov.otp_id);
    try {
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/ledger/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otp_id: mov.otp_id }),
      });
      if (res.ok) {
        onBalancesChanged?.();
        refreshAudit();
        await fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`No se pudo liberar: ${err.detail || res.status}`);
      }
    } catch (e) { alert('Error de red al liberar'); } finally { setReleasing(null); }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const next = search.trim();
    if (page === 1 && appliedSearch === next) fetchData();
    else { setPage(1); setAppliedSearch(next); }
  };

  const money = (v) => `$${Number(v || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const renderEstado = (estado) => {
    const colors = {
      COMPLETADA: 'bg-emerald-100 text-emerald-800',
      RECHAZADA: 'bg-red-100 text-red-700',
      PENDIENTE: 'bg-amber-100 text-amber-700',
    };
    return <span className={`text-[10px] px-1.5 py-0.5 rounded ${colors[estado] || 'bg-gray-100 text-gray-700'} font-semibold`}>{estado}</span>;
  };

  const renderTipo = (tipo) => (
    tipo === 'ENT'
      ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">ENT</span>
      : <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-semibold">SAL</span>
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="ledger-title" data-testid="otp-ledger-modal">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30">
          <BookOpen className="w-5 h-5 text-amber-700" />
          <div className="flex-1">
            <h2 id="ledger-title" data-testid="ledger-title" className="text-base font-bold text-gray-900 dark:text-gray-100">Ledger 1TouchPay — {cvuFilter ? `CVU ${cvuFilter}` : 'Global'}</h2>
            <p className="text-[11px] text-gray-600 dark:text-gray-400">Fuente de verdad propia · firmada con HMAC · {pagination.total ?? 0} movimientos</p>
          </div>
          <button onClick={handleExport} disabled={exporting} className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium transition-colors flex items-center gap-1.5" data-testid="otp-ledger-export-btn" title="Exportar todo el ledger a Excel">
            <Download className="w-3.5 h-3.5" /> {exporting ? 'Generando…' : 'Exportar Excel'}
          </button>
          <button onClick={onClose} aria-label="Cerrar ledger" className="p-1.5 hover:bg-amber-200 rounded-md transition-colors" data-testid="otp-ledger-close">
            <X className="w-4 h-4 text-gray-700" />
          </button>
        </div>

        <div className="px-5 py-1.5 text-[11px] text-gray-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="ledger-audit-freshness">
          <span data-testid="ledger-audit-updated">{auditLoading ? 'Actualizando auditoría…' : checkedAt ? `Auditoría consultada ${new Date(checkedAt).toLocaleTimeString('es-AR')}` : 'Auditoría sin datos'}</span>
          {auditError && <span role="alert" data-testid="ledger-audit-error" className="text-amber-700">Auditoría o cuadre sin actualizar. La tabla sigue disponible.</span>}
        </div>
        {/* Audit / Cuadre */}
        {audit && audit.saldo && (
          <div className="px-5 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
            <div>
              <p className="text-gray-500">Saldo ledger</p>
              <p className="font-bold text-gray-900 dark:text-gray-100" data-testid="ledger-saldo-local">{money(audit.saldo.ledger_local)}</p>
            </div>
            <div>
              <p className="text-gray-500">Saldo API OTP</p>
              <p className="font-bold text-gray-900 dark:text-gray-100" data-testid="ledger-saldo-otp">{audit.saldo.reportado_otp_total != null ? money(audit.saldo.reportado_otp_total) : '—'}</p>
            </div>
            <div>
              <p className="text-gray-500">Descuadre</p>
              <p className={`font-bold ${audit.saldo.cuadrado ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="ledger-descuadre">
                {audit.saldo.descuadre != null ? money(audit.saldo.descuadre) : '—'} {audit.saldo.cuadrado ? '✓' : ''}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Pendientes / Consumidos (global)</p>
              <p className="font-bold text-gray-900 dark:text-gray-100">
                <span className="text-amber-700">{audit.consumed_by_receipt?.pending ?? 0}</span> / <span className="text-emerald-700">{audit.consumed_by_receipt?.consumed ?? 0}</span>
              </p>
            </div>
            <div>
              <p className="text-gray-500">Sellos HMAC</p>
              <p className="font-bold flex items-center gap-1">
                {audit.tamper_check?.bad_seal === 0
                  ? <span className="text-emerald-700"><ShieldCheck className="inline w-3 h-3" /> {audit.tamper_check?.checked} OK</span>
                  : <span className="text-red-700"><ShieldAlert className="inline w-3 h-3" /> {audit.tamper_check?.bad_seal} ⚠</span>}
              </p>
            </div>
          </div>
        )}

        {/* Auditoría 3 vías — webhook vs ledger (cruce cada 3hs en el worker) */}
        {cuadre && cuadre.tres_vias && (() => {
          const tv = cuadre.tres_vias;
          const ok = (tv.n_webhook_sin_ledger || 0) === 0 && (tv.n_monto_distinto || 0) === 0;
          const ts = cuadre.tres_vias_ts ? new Date(cuadre.tres_vias_ts).toLocaleString('es-AR', { hour12: false }) : '—';
          return (
            <div className="px-5 py-2 bg-indigo-50/70 dark:bg-indigo-900/10 border-b border-gray-200 dark:border-gray-700 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs" data-testid="ledger-tres-vias">
              <div>
                <p className="text-gray-500">Auditoría 3 vías</p>
                <p className={`font-bold ${ok ? 'text-emerald-700' : 'text-red-700'}`} data-testid="tres-vias-semaforo">
                  {ok ? '✓ Sin diferencias' : '⚠ Diferencias graves'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Confiabilidad webhook OTP</p>
                <p className="font-bold text-gray-900 dark:text-gray-100" data-testid="tres-vias-confiabilidad">
                  {tv.confiabilidad_webhook_pct != null ? `${tv.confiabilidad_webhook_pct}%` : '—'}
                  <span className="font-normal text-gray-500"> ({(tv.ledger_ent_desde_webhook || 0) - (tv.ledger_sin_webhook || 0)}/{tv.ledger_ent_desde_webhook || 0} ENT notificados)</span>
                </p>
              </div>
              <div>
                <p className="text-gray-500">Webhook sin respaldo API</p>
                <p className={`font-bold ${(tv.n_webhook_sin_ledger || 0) === 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="tres-vias-sin-ledger">{tv.n_webhook_sin_ledger || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Montos distintos</p>
                <p className={`font-bold ${(tv.n_monto_distinto || 0) === 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="tres-vias-monto-distinto">{tv.n_monto_distinto || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Último cruce</p>
                <p className="font-bold text-gray-900 dark:text-gray-100" data-testid="tres-vias-ts">{ts}</p>
              </div>
            </div>
          );
        })()}

        {/* Filtros */}
        <div className="px-5 py-2.5 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            {['all', 'pending', 'consumed'].map((s) => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`text-[11px] px-3 py-1 font-medium border ${statusFilter === s ? 'bg-amber-500 text-white border-amber-500' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-50'} ${s === 'all' ? 'rounded-l-md' : s === 'consumed' ? 'rounded-r-md' : ''}`}
                data-testid={`ledger-filter-${s}`}>
                {s === 'all' ? 'Todos' : s === 'pending' ? 'Pending' : 'Consumed'}
              </button>
            ))}
          </div>

          <select value={cvuFilter} onChange={(e) => { setCvuFilter(e.target.value); setPage(1); }} className="text-[11px] px-2 py-1 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:text-gray-200" data-testid="ledger-filter-cvu">
            <option value="" data-testid="ledger-cvu-all">CVU: Todos</option>
            {cvuOptions.map(c => <option key={c.id} value={c.id} data-testid={`ledger-cvu-option-${c.id}`}>{c.id}{c.alias ? ` · ${c.alias}` : ''}</option>)}
          </select>

          <select value={tipoFilter} onChange={(e) => { setTipoFilter(e.target.value); setPage(1); }} className="text-[11px] px-2 py-1 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:text-gray-200" data-testid="ledger-filter-tipo">
            <option value="">Tipo: Todos</option>
            <option value="ENT">ENT (crédito)</option>
            <option value="SAL">SAL (débito)</option>
          </select>

          <select value={estadoFilter} onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); }} className="text-[11px] px-2 py-1 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:text-gray-200" data-testid="ledger-filter-estado">
            <option value="">Estado: Todos</option>
            <option value="COMPLETADA">COMPLETADA</option>
            <option value="RECHAZADA">RECHAZADA</option>
            <option value="PENDIENTE">PENDIENTE</option>
          </select>

          <form onSubmit={handleSearchSubmit} className="flex-1 max-w-md">
            <div className="relative">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar titular, CUIT o coelsaId..."
                className="text-[11px] w-full pl-7 pr-2 py-1 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:text-gray-200"
                data-testid="ledger-search-input"
              />
              <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            </div>
          </form>

          <button onClick={() => { fetchData(); refreshAudit(); }} disabled={loading} className="text-[11px] px-2 py-1 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-800 font-medium disabled:opacity-50" data-testid="ledger-refresh">
            <RefreshCw className={`inline w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Cargando' : 'Refrescar'}
          </button>
        </div>

        {ledgerError && <p role="alert" data-testid="ledger-load-error" className="px-5 py-2 text-sm text-red-700">{ledgerError}</p>}
        {/* Tabla */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800 z-10">
              <tr>
                <th className="px-2 py-2 text-left text-gray-700 dark:text-gray-300 font-semibold">Fecha</th>
                <th className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 font-semibold">CVU</th>
                <th className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 font-semibold">Tipo</th>
                <th className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 font-semibold">Estado</th>
                <th className="px-2 py-2 text-right text-gray-700 dark:text-gray-300 font-semibold">Importe</th>
                <th className="px-2 py-2 text-right text-gray-700 dark:text-gray-300 font-semibold">Cargos</th>
                <th className="px-2 py-2 text-right text-gray-700 dark:text-gray-300 font-semibold">Neto</th>
                <th className="px-2 py-2 text-left text-gray-700 dark:text-gray-300 font-semibold">Titular</th>
                <th className="px-2 py-2 text-left text-gray-700 dark:text-gray-300 font-semibold">CUIT</th>
                <th className="px-2 py-2 text-left text-gray-700 dark:text-gray-300 font-semibold">Detalle / Cliente</th>
                <th className="px-2 py-2 text-center text-gray-700 dark:text-gray-300 font-semibold">Estado match</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-8 text-gray-500">Cargando ledger...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-8 text-gray-500">Sin movimientos para los filtros aplicados.</td></tr>
              ) : items.map((m, i) => {
                const isPending = m.tipo === 'ENT' && m.estado === 'COMPLETADA' && !m.consumed_by_receipt_id;
                const isConsumed = !!m.consumed_by_receipt_id;
                return (
                  <tr key={m.otp_id || i} className="border-b border-gray-100 dark:border-gray-800 hover:bg-amber-50 dark:hover:bg-amber-900/10" data-testid={`ledger-row-${m.otp_id}`}>
                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{(m.created_at || '').slice(0, 16)}</td>
                    <td className="px-2 py-1.5 text-center text-gray-600">{m.cvu_id}</td>
                    <td className="px-2 py-1.5 text-center">{renderTipo(m.tipo)}</td>
                    <td className="px-2 py-1.5 text-center">{renderEstado(m.estado)}</td>
                    <td data-testid={`ledger-importe-${m.otp_id}`} className="px-2 py-1.5 text-right font-mono text-gray-900 dark:text-gray-100">{money(m.importe)}</td>
                    <td data-testid={`ledger-cargos-${m.otp_id}`} className="px-2 py-1.5 text-right font-mono text-red-600 text-[11px]">{Number(m.total_descuentos) ? `-${money(m.total_descuentos)}` : '—'}</td>
                    <td data-testid={`ledger-neto-${m.otp_id}`} className="px-2 py-1.5 text-right font-mono font-semibold text-emerald-700">{money(m.neto_acreditado)}</td>
                    <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200 max-w-[200px] truncate" title={m.titular}>{m.titular || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400 font-mono text-[10px]">{m.cuit || '—'}</td>
                    <td className="px-2 py-1.5">
                      {m.tipo === 'SAL'
                        ? <LedgerSalidaDetalle mov={m} clientes={clientes} token={token} onSaved={handleRowSaved} />
                        : isPending
                          ? <LedgerSugerencia s={m._sugerencia} movId={m.otp_id} />
                          : <span className="text-[10px] text-gray-300">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      {isPending && (
                        <div className="flex flex-col items-center gap-1">
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"><Unlock className="w-3 h-3" /> Pending</span>
                          <button onClick={() => openMatch(m)} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors" data-testid={`ledger-match-btn-${m.otp_id}`}>Conciliar</button>
                        </div>
                      )}
                      {isConsumed && (
                        <div className="flex flex-col items-center gap-0.5">
                          {m._receipt_info?.synced === false ? (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold" title="Consumido pero SIN match real en el dashboard (desincronizado)" data-testid={`ledger-desync-badge-${m.otp_id}`}><Lock className="w-3 h-3" /> Sin match real</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold"><Lock className="w-3 h-3" /> Consumed</span>
                          )}
                          {m._receipt_info && (
                            <span className="text-[9px] text-gray-500">{m._receipt_info.cliente_name?.slice(0, 16)}</span>
                          )}
                          <button onClick={() => doRelease(m)} disabled={releasing === m.otp_id}
                            className={`text-[9px] px-1.5 py-0.5 rounded text-white font-medium transition-colors disabled:opacity-50 ${m._receipt_info?.synced === false ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400 hover:bg-gray-500'}`}
                            title="Liberar este movimiento: vuelve a Pending y desvincula el comprobante"
                            data-testid={`ledger-release-btn-${m.otp_id}`}>
                            {releasing === m.otp_id ? '…' : 'Liberar'}
                          </button>
                        </div>
                      )}
                      {!isPending && !isConsumed && (
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-[10px] text-gray-400">—</span>
                          <button onClick={() => openMatch(m)} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors" data-testid={`ledger-match-btn-${m.otp_id}`}>Conciliar</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer: paginación */}
        <div className="px-5 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between text-xs">
          <div data-testid="ledger-pagination-status" className="text-gray-600 dark:text-gray-400">
            Página {pagination.page || 1} de {pagination.pages || 1} · {pagination.total || 0} movimientos
          </div>
          <div className="flex gap-1">
            <button disabled={(pagination.page || 1) <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100" data-testid="ledger-prev-page">Anterior</button>
            <button disabled={(pagination.page || 1) >= (pagination.pages || 1) || loading} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100" data-testid="ledger-next-page">Siguiente</button>
          </div>
        </div>
      </div>

      {/* Modal selector de conciliación manual */}
      {matchMov && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="ledger-match-modal" onClick={() => !matching && setMatchMov(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-gray-900 dark:text-gray-100">Conciliar movimiento manualmente</h4>
                <p className="text-xs text-gray-500">Mov #{matchMov.otp_id} · {money(matchMov.importe)} · {matchMov.titular || '—'} · {(matchMov.created_at || '').slice(0, 16)}</p>
              </div>
              <button onClick={() => !matching && setMatchMov(null)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" data-testid="ledger-match-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-2 border-b border-gray-200 dark:border-gray-700 flex gap-2">
              <input value={matchSearch} onChange={(e) => setMatchSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchCandidates()} placeholder="Buscar por nombre origen/destino… (vacío = mismo monto)" className="flex-1 text-sm px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700" data-testid="ledger-match-search" />
              <button onClick={searchCandidates} className="text-sm px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-800 text-white">Buscar</button>
            </div>
            {candidatesError && <p role="alert" data-testid="ledger-candidates-error" className="px-5 py-2 text-sm text-red-700">{candidatesError}</p>}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {loadingCand ? (
                <p className="text-center py-8 text-gray-500 text-sm">Buscando comprobantes…</p>
              ) : candidates.length === 0 && !candidatesError ? (
                <p className="text-center py-8 text-gray-500 text-sm">No hay comprobantes sin conciliar para este criterio.</p>
              ) : (
                <div className="space-y-2">
                  {candidates.map((r) => {
                    const sameAmount = Number(r.amount) === Number(matchMov.importe);
                    return (
                      <div key={r.id} className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border ${sameAmount ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-900/10' : 'border-gray-200 dark:border-gray-700'}`} data-testid={`ledger-candidate-${r.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{money(r.amount)} {sameAmount && <span className="text-[10px] text-emerald-600 font-normal">· monto coincide</span>}</p>
                          <p className="text-xs text-gray-500 truncate">{r.cliente_nombre || '(sin cliente)'} · {r.date} {r.time} · {r.origin_name || r.destination_holder || '—'} {r.has_wa && '· 📱'}</p>
                        </div>
                        <button onClick={() => doMatch(r)} disabled={matching} className="text-xs px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium flex-shrink-0" data-testid={`ledger-confirm-match-${r.id}`}>{matching ? '…' : 'Conciliar'}</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="px-5 py-2 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-500">
              ⚠️ Match libre: se concilia aunque el monto/identidad no coincidan. Dispara la reacción ✅ en WhatsApp y actualiza el Dashboard.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
